import type { ButtonProps } from "@/types";
import Button from "@ui/button";
import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";

interface StatusScreenProps {
  title: string;
  subtitle?: string;
  description?: string;
  icon: LucideIcon;
  badge?: string;
  bgText?: string;
  actions?: {
    label: string;
    icon?: LucideIcon;
    onClick?: () => void;
    to?: string;
    design?: ButtonProps["design"];
  }[];
}

export default function StatusScreen({
  title,
  subtitle,
  description,
  icon: Icon,
  badge,
  bgText,
  actions = [],
}: StatusScreenProps) {
  return (
    <div className="relative min-h-dvh w-full flex items-center justify-center bg-background p-6 overflow-hidden transition-colors duration-700">
      {/* 1. Fondo Atmosférico (Glows y Grid) */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] h-125 w-125 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-10%] h-125 w-125 rounded-full bg-primary/10 blur-3xl" />
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 max-w-2xl w-full text-center space-y-10"
      >
        {/* 2. Icono con Badge */}
        <div className="relative mx-auto w-32 h-32 flex items-center justify-center">
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="glass-card w-24 h-24 rounded-[2.5rem]! flex items-center justify-center border-white/10 shadow-2xl bg-muted backdrop-blur-xl"
          >
            <Icon size={40} className="text-muted" strokeWidth={1.5} />
          </motion.div>

          {badge && (
            <motion.span
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 12 }}
              className="absolute -bottom-2 -right-2 bg-primary text-white text-bubbly text-xl px-4 py-1 rounded-2xl shadow-xl"
            >
              {badge}
            </motion.span>
          )}
        </div>

        {/* 3. Textos */}
        <div className="space-y-4">
          <h1 className="text-5xl md:text-7xl text-primary leading-none">
            {title}
          </h1>
          {subtitle && (
            <p className="label-nano opacity-40 max-w-sm mx-auto">{subtitle}</p>
          )}
          {description && (
            <p className="text-body mx-auto max-w-md">{description}</p>
          )}
        </div>

        {/* 4. Acciones Dinámicas */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
          {actions.map((action, idx) => (
            <Button
              key={idx}
              design={action.design || (idx === 0 ? "outline" : "default")}
              size="lg"
              to={action.to}
              onClick={action.onClick}
              icon={action.icon}
              className="w-full sm:w-auto rounded-full! px-10"
            >
              {action.label}
            </Button>
          ))}
        </div>
      </motion.div>

      {/* 5. Marca de agua de fondo */}
      {bgText && (
        <div className="fixed -bottom-10 left-1/2 -translate-x-1/2 opacity-[0.03] pointer-events-none select-none">
          <span className="text-bubbly text-[25vw] leading-none">{bgText}</span>
        </div>
      )}
    </div>
  );
}
